<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import BaseAnnouncementBar from '@/components/BaseAnnouncementBar.vue';
import {useAnnouncementsI18n} from '@/features/announcements/i18n';
import {dismissAnnouncement, getAnnouncements} from '@/features/announcements/announcementsApi';
import {useAnnouncementPoller, withoutExpired} from '@/features/announcements/useAnnouncementPoller';
import {notifyAnnouncementsShown, onAnnouncementsChanged} from '@/features/announcements/announcementsChanged';
import {canReach, navigateTo} from '@/navigation/payrollNavigator';
import type {Announcement} from '@/features/announcements/types';

defineOptions({name: 'TheAnnouncementBar'});

const props = withDefaults(defineProps<{
    companyId: number;
    announcements?: Announcement[];
}>(), {
    announcements: () => [],
});

const {t} = useAnnouncementsI18n();

// Everything the mailbox holds, including announcements that do not render: a silent wait still
// has to arm the poller, and a dismissed one still has to receive its outcome.
const entries = ref<Announcement[]>(withoutExpired([...props.announcements], Date.now()));
const shown = computed(() => entries.value.filter(a => a.visible));
const host = ref<HTMLElement | null>(null);

async function refresh(signal?: AbortSignal): Promise<void> {
    entries.value = withoutExpired(await getAnnouncements(props.companyId, signal), Date.now());
}

// Runs only while something is pending, and stops the moment nothing is. See the composable for
// why an armed poller is affordable where a per-user interval would not be.
useAnnouncementPoller(entries, refresh);

// The poller is armed by an entry this bar already holds, so it can never discover the first one.
// A feature that raises an announcement from an XHR says so, and this fetch arms everything after.
let stopListening: (() => void) | null = null;

// The main menu is position:fixed and would sit on top of the bar, swallowing its clicks. Publish
// the bar's real height so the menu can drop by exactly that much — measured rather than hardcoded
// because a long message wraps, and several announcements stack.
const NAV_OFFSET_PROPERTY = '--merit-announcement-bar-height';

let observer: ResizeObserver | null = null;

// Both conditions, because they part company for a tick: the template ref is cleared a beat after
// the last announcement goes, and measuring the element in between would republish a stale height.
function publishHeight() {
    if (!host.value || shown.value.length === 0) {
        clearHeight();
        return;
    }

    document.documentElement.style.setProperty(NAV_OFFSET_PROPERTY, `${host.value.offsetHeight}px`);
}

function clearHeight() {
    document.documentElement.style.removeProperty(NAV_OFFSET_PROPERTY);
}

// The host is v-if'd, so it comes and goes as announcements do — and on most pages it is absent at
// mount, because the mailbox is empty until something is raised. Following the element rather than
// measuring once means an announcement arriving later still moves the menu down; measuring only at
// mount left the offset at zero and the bar covering the menu it was supposed to sit above.
function trackHost() {
    observer?.disconnect();
    publishHeight();
    if (observer && host.value) observer.observe(host.value);
}

onMounted(() => {
    // A failed arming fetch is left alone: the next page load renders the bar from the server, and
    // retrying here would be a second poller with none of the first one's termination rules.
    stopListening = onAnnouncementsChanged(() => { refresh().catch(() => {}); });

    document.addEventListener('click', onMenuClick, true);

    if (typeof ResizeObserver !== 'undefined') observer = new ResizeObserver(publishHeight);
    trackHost();
});

// Moving on in the menu is taken as having read the good news, so nobody has to close a bar that
// only says something went right. Capture, because the legacy menu stops the click on its way up.
// A finding is left alone — going somewhere else is not dealing with it.
const MAIN_MENU = '.main-navigation-div';

function onMenuClick(event: MouseEvent) {
    const clicked = event.target as Element | null;
    if (!clicked || typeof clicked.closest !== 'function' || !clicked.closest(MAIN_MENU)) return;

    dismissRead();
}

function dismissRead() {
    const read = shown.value.filter(a => a.severity === 'success');
    if (read.length === 0) return;

    entries.value = entries.value.map(a =>
        a.severity === 'success' && a.visible ? {...a, visible: false} : a);

    // Not awaited and never retried: the click is usually a navigation, and the user has already
    // seen the bar go. keepalive is what carries the request past the page that started it.
    for (const announcement of read) {
        dismissAnnouncement(props.companyId, announcement.id, true).catch(() => {});
    }
}

watch([shown, host], trackHost, {flush: 'post'});

// For views that can answer an announcement themselves. Only the bar knows what is on screen, and
// a view standing where one points has no other way to find out that it arrived.
watch(shown, () => notifyAnnouncementsShown(
    shown.value.map(a => a.linkTarget).filter((target): target is string => Boolean(target))));

onBeforeUnmount(() => {
    stopListening?.();
    stopListening = null;
    document.removeEventListener('click', onMenuClick, true);
    observer?.disconnect();
    observer = null;
    clearHeight();
});

function textOf(announcement: Announcement): string {
    const text = t(announcement.translationKey);
    if (!announcement.parameters) return text;

    return Object.keys(announcement.parameters).reduce(
        (result, name) => result.split(`{${name}}`).join(announcement.parameters![name]),
        text,
    );
}

// A link is offered only for somewhere the user can actually end up: the destination has to exist
// on this page and be permitted. Otherwise the announcement still shows — knowing the declaration
// came back with findings matters even to someone who cannot open the view — just as plain text.
function linkedTo(announcement: Announcement): string | null {
    return canReach(announcement.linkTarget) ? announcement.linkTarget : null;
}

// Dismissed before leaving, not after: reaching a destination on another page is a full page load,
// which would cut the request off mid-flight. A user who has gone to deal with the thing does not
// want to be told about it again on arrival.
async function follow(announcement: Announcement) {
    const target = linkedTo(announcement);
    if (!target) return;

    await dismiss(announcement);
    navigateTo(target, announcement.linkAction);
}

// Every bar closes. What that means differs: closing a settled announcement is done with it, while
// closing one that is still waiting only hides the waiting message — the server keeps the entry, so
// the outcome still arrives.
async function dismiss(announcement: Announcement) {
    // Hide first so the bar goes immediately, then take the server's answer as truth. That answer
    // is a fresh read, so dismissing also surfaces anything that arrived since the page rendered —
    // the layout only re-renders on a full page load, and sub-page navigation is AJAX.
    entries.value = entries.value.map(a =>
        a.id === announcement.id ? {...a, visible: false} : a);

    try {
        entries.value = await dismissAnnouncement(props.companyId, announcement.id);
    } catch {
        // The bar is already gone for this page view; the next page load re-reads the mailbox and
        // brings it back, which is the honest outcome of a failed dismissal.
    }
}
</script>

<template>
    <div v-if="shown.length" ref="host" class="merit-announcement-bar-host" data-test="announcement-bar-host">
        <BaseAnnouncementBar
            v-for="announcement in shown"
            :key="announcement.id"
            :variant="announcement.severity"
            :close-aria-label="t('lbl-announc-bar-close')"
            :data-test="`announcement-${announcement.id}`"
            @dismiss="dismiss(announcement)"
        >
            <button
                v-if="linkedTo(announcement) && !announcement.linkTextKey"
                type="button"
                @click="follow(announcement)"
            >{{ textOf(announcement) }}</button>
            <template v-else>
                {{ textOf(announcement) }}
                <button
                    v-if="linkedTo(announcement)"
                    type="button"
                    @click="follow(announcement)"
                >{{ t(announcement.linkTextKey!) }}</button>
            </template>
        </BaseAnnouncementBar>
    </div>
</template>

<style scoped>
/* Fixed rather than in flow, because the main menu below it is fixed too. In flow the bar would
   scroll away while the menu stayed at top: <bar height>, leaving an empty strip across the top of
   the viewport with content scrolling behind it. Space is reserved by .main-navigation-placeholder,
   which grows by the same variable this component publishes. */
.merit-announcement-bar-host {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    /* Above the desktop menu (3), below the mobile menu (10) — which covers the bar when open. */
    z-index: 4;
    display: flex;
    flex-direction: column;
}
</style>
